import { Box, Input, InputLabel } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import SimpleModal from "@/components/SimpleModal.tsx";
import { ipcRendererSend, isElectron } from "@/utils";

/**
 * Allows a user to change the URL of the active environment in the Electron app.
 */
export const ChangeEnvironmentUrl = () => {
  const [customUrl, setCustomUrl] = useState<string>("");
  const [activeKey, setActiveKey] = useState<string>(window.location.host);
  const [open, setOpen] = useState<boolean>(false);

  /**
   * Listen for hot-keys to open/close the modal
   */
  useHotkeys(["meta+shift+e", "ctrl+shift+e"], () => {
    setOpen((value) => !value);
  });

  const currentActiveHost = window.location.host;

  const allowedBaseUrls = ["storyboard.com", "storyboardtesting.com", "localhost"];

  const lastCharacterIsSlash = customUrl.slice(-1) === "/";

  const isValidBaseUrl =
    customUrl?.length === 0
      ? true
      : !lastCharacterIsSlash && customUrl && allowedBaseUrls.some((url) => customUrl.includes(url));

  const environments = [
    { key: "app.storyboard.com", name: "Production", url: "https://app.storyboard.com" },
    { key: "staging.app.storyboardtesting.com", name: "Staging", url: "https://staging.app.storyboardtesting.com" },
    { key: "dev.app.storyboardtesting.com", name: "Dev", url: "https://dev.app.storyboardtesting.com" },
    { key: "localhost:3000", name: "Localhost", url: "http://localhost:3000" },
    { key: "other", name: "Other", url: "other" },
  ].map((env) => ({
    ...env,
    active: env.url.includes(activeKey),
  }));

  const activeEnvironment = environments.find((env) => env.active);

  const disabledButton =
    currentActiveHost === activeKey ||
    (activeEnvironment?.key === "other" && (customUrl?.length === 0 || !isValidBaseUrl));

  const changeBaseUrl = () => {
    if (activeEnvironment.key === "other") {
      ipcRendererSend("change-environment", customUrl);
    } else {
      ipcRendererSend("change-environment", activeEnvironment.url);
    }
  };

  if (!isElectron) {
    return <></>;
  }

  return (
    <SimpleModal open={open} closeModal={() => setOpen(false)} title="Change Environment">
      <div>
        <Box sx={{ display: "flex", gap: 1 }}>
          {environments.map((env) => (
            <Box
              key={env.url}
              component="button"
              sx={{ px: 2, py: 1, flexGrow: 1, justifyContent: "center" }}
              className={`basic-button grays ${env.active ? "active" : ""}`}
              onClick={() => setActiveKey(env.key)}
            >
              {env.name}
            </Box>
          ))}
        </Box>
        {activeEnvironment?.key === "other" && (
          <Box sx={{ mt: 2 }}>
            <InputLabel htmlFor="customUrl">Custom URL</InputLabel>
            <Input
              sx={{ width: "100%" }}
              id="customUrl"
              error={!isValidBaseUrl}
              value={customUrl}
              onInput={(e: ChangeEvent<HTMLInputElement>) => setCustomUrl(() => e.target.value)}
            />
          </Box>
        )}
        <Box sx={{ textAlign: "right", mt: 2 }}>
          <Box
            disabled={disabledButton}
            onClick={() => changeBaseUrl()}
            component="button"
            className="basic-button"
            sx={{ borderRadius: 2, border: "solid 1px #666", px: 2, py: 2 }}
          >
            Change Environment
          </Box>
        </Box>
      </div>
    </SimpleModal>
  );
};
